import { useUIStore } from '@/stores/uiStore';
import { useEditorStore } from '@/stores/editorStore';
import { EMOJI_CATEGORIES } from '@/lib/constants/emojis';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { clsx } from 'clsx';

export function EmojiPicker() {
  const isOpen = useUIStore((s) => s.isEmojiPickerOpen);
  const close = useUIStore((s) => s.setEmojiPickerOpen);
  const { quillInstance } = useEditorStore();
  const [activeCategory, setActiveCategory] =
    useState<keyof typeof EMOJI_CATEGORIES>('PROFESSIONAL');
  const panelRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        close(false);
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, close]);

  const insertEmoji = (emoji: string) => {
    if (quillInstance) {
      const selection = quillInstance.getSelection();
      if (selection) {
        quillInstance.insertText(selection.index, emoji);
        quillInstance.setSelection(selection.index + emoji.length);
      } else {
        // Append if no selection
        const length = quillInstance.getLength();
        quillInstance.insertText(length - 1, emoji);
      }
    }
    // Optional: close after selection
    // close(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm p-4">
      <div
        ref={panelRef}
        className="bg-card rounded-xl shadow-2xl w-full max-w-sm flex flex-col max-h-[600px] animate-fade-in-up"
      >
        <div className="p-4 border-b border-border flex items-center justify-between">
          <h3 className="font-semibold text-text-primary">Insert Emoji</h3>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => close(false)}>
            <X className="w-4 h-4 text-text-primary" />
          </Button>
        </div>

        {/* Categories */}
        <div className="flex overflow-x-auto p-2 gap-1 border-b border-border no-scrollbar">
          {Object.entries(EMOJI_CATEGORIES).map(([key, cat]) => (
            <button
              key={key}
              className={clsx(
                'px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors',
                activeCategory === key
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-background hover:bg-accent/10 text-text-secondary',
              )}
              onClick={() => setActiveCategory(key as any)}
            >
              {cat.name}
            </button>
          ))}
        </div>

        <div className="p-4 overflow-y-auto flex-1">
          <div className="grid grid-cols-6 gap-2">
            {EMOJI_CATEGORIES[activeCategory].emojis.map((emoji) => (
              <button
                key={emoji}
                className="text-2xl hover:bg-background p-2 rounded transition-transform hover:scale-110"
                onClick={() => insertEmoji(emoji)}
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
